/*
 * FIT ENGINE — the browser copy of src/10_fit/match.py.
 *
 * THIS FILE IS A DEPLOYMENT OF A SPECIFICATION, NOT AN INDEPENDENT ENGINE.
 * match.py is the specification; src/10_fit/test_conformance.py runs the two
 * against the same fixtures under node and fails the build when they disagree.
 * A second implementation of one rule is the fault this repo has paid for three
 * times (three retry policies, GENERATION_MODE read by one of two entry points,
 * PIPELINE_PATHS omitting src/6_print) — so this one is TESTED to be the same
 * rule rather than trusted to be.
 *
 * EVERY NUMBER COMES FROM THE INDEX, NOT FROM THIS FILE. The weights, the style
 * matrix, the floor priors and the contrast curve all travel in fit_index.json.
 * Re-fitting them is then a data change with no theme deploy, and the two
 * engines cannot read different constants because there is only one copy.
 *
 * IT RUNS ON EVERY KEYSTROKE AND COSTS NOTHING. ~800 candidates scored in well
 * under a millisecond, so a tap re-ranks an already-visible grid rather than
 * fetching anything. That is the whole reason the ranking is deterministic code
 * and not a model call: a per-request model call makes the bill a function of
 * browsing.
 */
(function (global) {
  'use strict';

  /* ── the contrast curve ─────────────────────────────────────────────────
   * OPPOSITE SIGN TO EVERY HARMONY TERM. A rug is a figure on a ground:
   * figural preference rises with CONTRAST while pair harmony rises with
   * SIMILARITY. Conflating them is how "cream rug on light oak" scores 0.9. */
  function hFloor(dl, w) {
    if (dl <= w.disappear_dl) return 0;
    var s = Math.min(1, (dl - w.disappear_dl) / (w.full_dl - w.disappear_dl));
    if (dl > w.hole_dl) s *= Math.max(w.hole_floor, 1 - (dl - w.hole_dl) / 45);
    return s;
  }

  /* Acklam's inverse normal CDF. Present so the posterior is a deterministic
   * quantile grid rather than random draws — a recommender that answers
   * differently on a refresh is one nobody can debug, and the same grid is what
   * match.py builds, which is what makes the two comparable at all. */
  function probit(p) {
    var a = [-3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02,
             1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
    var b = [-5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02,
             6.680131188771972e+01, -1.328068155288572e+01];
    var c = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00,
             -2.549732539343734e+00, 4.374664141464968e+00, 2.938163982698783e+00];
    var d = [7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00,
             3.754408661907416e+00];
    var plow = 0.02425, phigh = 1 - plow, q, r;
    p = Math.min(1 - 1e-12, Math.max(1e-12, p));
    if (p < plow) {
      q = Math.sqrt(-2 * Math.log(p));
      return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
             ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
    }
    if (p > phigh) {
      q = Math.sqrt(-2 * Math.log(1 - p));
      return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
              ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
    }
    q = p - 0.5; r = q * q;
    return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q /
           (((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1);
  }

  var POSTERIOR_N = 200;

  /* Plausible floor lightnesses — a MIXTURE, not a Gaussian, because the
   * residual error is the MATERIAL being read wrong (a large discrete jump)
   * and not measurement noise. Returns [] when nothing is known, and an empty
   * posterior makes the contrast term ABSTAIN rather than default. */
  function floorPosterior(idx, material, anchorL, anchorSd) {
    var mats = idx.floor_materials, misread = idx.material_misread_p;
    var prior = mats[material || ''] || null;
    if (!prior && (anchorL === null || anchorL === undefined)) return [];
    var mu, sd;
    if (prior && anchorL !== null && anchorL !== undefined) {
      var sdA = anchorSd || 6, w1 = 1 / (prior[1] * prior[1]), w2 = 1 / (sdA * sdA);
      mu = (prior[0] * w1 + anchorL * w2) / (w1 + w2);
      sd = Math.sqrt(1 / (w1 + w2));
    } else if (prior) { mu = prior[0]; sd = prior[1]; }
    else { mu = anchorL; sd = anchorSd || 6; }

    var out = [], main = Math.round(POSTERIOR_N * (1 - misread)), i;
    for (i = 0; i < main; i++) out.push(mu + sd * probit((i + 0.5) / main));

    var others = Object.keys(mats).filter(function (m) { return m !== material; });
    var tail = POSTERIOR_N - main;
    for (i = 0; i < tail; i++) {
      var m = mats[others[i % others.length]];
      var q = (Math.floor(i / others.length) + 0.5) /
              Math.max(1, tail / others.length);
      out.push(m[0] + m[1] * probit(Math.min(0.99, Math.max(0.01, q))));
    }
    return out.map(function (v) { return Math.min(98, Math.max(5, v)); });
  }

  /* THE 10th PERCENTILE, NOT A MEAN — the single most important line here.
   * It asks "how bad could this be?", which is the question the failure mode
   * poses. Measured: 1.39% of recommended rugs disappear under a point
   * estimate, 0.64% under this. Do not "simplify" it to an average. */
  function contrastScore(rugL, samples, w) {
    if (rugL === null || rugL === undefined || !samples.length) return null;
    var s = samples.map(function (f) { return hFloor(Math.abs(rugL - f), w); });
    s.sort(function (x, y) { return x - y; });
    var i = Math.round((w.contrast_pctl / 100) * (s.length - 1));
    return s[Math.max(0, Math.min(s.length - 1, i))];
  }

  function hueDistance(h1, h2) {
    var d = Math.abs(h1 - h2) % 360;
    return d <= 180 ? d : 360 - d;
  }

  function familyCentres(idx) {
    var out = {};
    idx.hue_families.forEach(function (f) {
      var n = f[0], lo = f[1], hi = f[2];
      out[n] = lo < hi ? (lo + hi) / 2 : (((lo + hi + 360) / 2) % 360);
    });
    return out;
  }

  function hFamily(idx, centres, rugFam, roomFam, w) {
    if (!rugFam || !roomFam) return null;
    if (rugFam === 'neutral' || roomFam === 'neutral') return w.neutral_cap;
    if (rugFam === roomFam) return 1;
    if (!(rugFam in centres) || !(roomFam in centres)) return null;
    var d = hueDistance(centres[rugFam], centres[roomFam]);
    if (d <= 60) return 1 - 0.35 * (d / 60);
    if (d >= 150) return 0.55 + 0.20 * ((d - 150) / 30);
    return 0.65 - 0.15 * ((d - 60) / 90);
  }

  function hAccent(rugNeutral, roomNeutral) {
    if (rugNeutral === null || rugNeutral === undefined) return null;
    if (roomNeutral === null || roomNeutral === undefined) return null;
    if (roomNeutral && !rugNeutral) return 1;
    if (roomNeutral && rugNeutral) return 0.70;
    if (!roomNeutral && rugNeutral) return 0.90;
    return 0.55;
  }

  function hPattern(rugBusy, roomLoad) {
    if (rugBusy === null || rugBusy === undefined) return null;
    if (roomLoad === null || roomLoad === undefined) return null;
    return Math.max(0, 1 - Math.abs(rugBusy - (1 - roomLoad)) / 0.8);
  }

  function styleScore(idx, roomStyle, rugStyle, w) {
    if (!roomStyle || !rugStyle) return null;
    var row = idx.style_matrix[String(roomStyle).toLowerCase()];
    if (!row) return null;
    var v = row[String(rugStyle).toLowerCase()];
    return v === undefined ? w.style_min : v;
  }

  /* A term that abstains is DROPPED and the remaining weights re-normalise.
   * A skipped input is never filled with a mid-range default: 0.5 is a claim,
   * an abstention is the absence of one. */
  function weighted(parts) {
    var tot = 0, acc = 0, n = 0;
    parts.forEach(function (p) {
      if (p[1] === null || p[1] === undefined) return;
      tot += p[0]; acc += p[0] * p[1]; n++;
    });
    return n ? [acc / tot, n] : [null, 0];
  }

  function scoreOne(idx, centres, p, room) {
    var w = idx.weights;
    var floor = contrastScore(p.L, room.floorSamples || [], w);
    var fam = hFamily(idx, centres, p.hue, room.colourFamily, w);
    var acc = hAccent(p.neu, room.isNeutral);

    var colour = weighted([[w.floor, floor], [w.family, fam], [w.accent, acc]])[0];
    if (colour !== null && p.neu) {
      /* The cap applies to the HARMONY half only. hFloor is re-added at full
       * strength, so a neutral rug on a neutral floor still scores as badly as
       * it deserves — which is the failure the cap must not hide. */
      var harm = weighted([[w.family, fam], [w.accent, acc]])[0];
      if (harm !== null && floor !== null) {
        var capped = Math.min(harm, w.neutral_cap);
        colour = (w.floor * floor + (w.family + w.accent) * capped) /
                 (w.floor + w.family + w.accent);
      }
    }

    var style = styleScore(idx, room.style, p.st, w);
    var pat = hPattern(p.busy, room.patternLoad);
    if (pat !== null && (p.busy || 0) < 0.12 && (room.patternLoad || 0) < 0.7) {
      pat = Math.min(pat, w.solid_cap);
    }

    var res = weighted([[w.style, style], [w.colour, colour], [w.pattern, pat]]);
    return {
      score: res[0], termsUsed: res[1],
      vetoed: floor !== null && floor < w.veto_below,
      parts: { style: style, colour: colour, pattern: pat,
               floor: floor, family: fam, accent: acc }
    };
  }

  /* Explanation clauses projected from the arithmetic through fixed templates.
   * Deterministic on purpose: a template cannot claim a reason the score did
   * not produce, and it is always spelled right. */
  function fill(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, function (_, k) {
      return vars[k] === undefined ? '' : vars[k];
    });
  }

  /* How this room's floor is named in a sentence. The analyser's vocabulary is
   * a CLASSIFIER's - "light tile", "carpet" - and reading it back verbatim
   * gives "your carpet floor". The mapping is in the INDEX so the two engines
   * cannot word it differently. */
  function floorPhrase(idx, room) {
    var m = idx.floor_phrases || {};
    return m[room.floorMaterial || ''] || idx.floor_fallback || 'floor';
  }

  function cap(s) {
    s = String(s || '');
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  }

  /* WHICH OF THE THREE FLOOR LEADS THIS ROOM GETS. `null` when the floor
   * lightness is unknown, and an unknown floor then has no tone lead at all -
   * it is never guessed. Mirrors match.py `_floor_band`. */
  function floorBand(idx, floorL) {
    if (floorL === null || floorL === undefined) return null;
    if (floorL < idx.floor_dark_max_l) return 'dark';
    return floorL >= idx.floor_light_min_l ? 'light' : 'mid';
  }

  /* THE OBSERVATIONS THAT COULD HONESTLY BE MADE ABOUT THIS RUG IN THIS ROOM,
   * STRONGEST FIRST, each gated on a FACT rather than on a score. Mirrors
   * match.py `_why`.
   *
   * IT RETURNS AXES, NOT SENTENCES. Each entry carries a `lead` (an
   * observation about the ROOM, true of all ten cards), a `text` (what THIS
   * rug does on that axis) and a `rider` (the same fact said short). The
   * CLIENT composes the paragraph, because only the client can see what the
   * cards above it already said - and ten cards share one room, so a per-card
   * sentence made every card open with the same observation and differ only in
   * adjectives, which is the thing this replaced.
   *
   * THE TEXT COMES FROM THE INDEX, never from a literal in this file. It used
   * to be literals, and this engine carried an em-dash where match.py had a
   * hyphen - identical in every other respect, and silently different in what
   * a customer reads. The conformance test caught it; removing the second copy
   * is what stops it recurring. */
  function why(idx, p, room, s) {
    var T = idx.reason_templates, out = [], f = s.parts;
    var fl = floorPhrase(idx, room);

    /* A WARNING OUTRANKS EVERY REASON AND IS RETURNED ALONE. A rug that may
     * vanish into the floor has nothing else worth saying about it. */
    if (s.vetoed) {
      return [{ axis: 'veto', kind: 'warn', lead: null,
                text: fill(T.veto, { floor: fl }), rider: null, v: 1 }];
    }

    /* TONE. Gated on the MEASURED separation, not on the term: `contrastScore`
     * is a 10th percentile over the floor posterior, so a rug can satisfy it
     * through the tail while sitting close to the median floor the sentence is
     * about to name. `tone_visible_dl` is not a new number - it is the index's
     * own `disappear_dl`, the line below which the difference cannot be seen. */
    var vis = idx.weights.tone_visible_dl;
    if (f.floor !== null && f.floor >= 0.75 && p.L !== null && p.L !== undefined &&
        room.floorL !== null && room.floorL !== undefined &&
        Math.abs(p.L - room.floorL) >= vis) {
      var way = p.L < room.floorL ? 'darker' : 'lighter';
      /* "MUCH" IS THE INDEX'S OWN full_dl, NOT A SECOND NUMBER - the
       * separation at which the contrast term reaches its ceiling, and so the
       * honest point at which to change the adverb. */
      var strong = Math.abs(p.L - room.floorL) >= idx.full_dl;
      var band = floorBand(idx, room.floorL);
      out.push({ axis: 'tone', kind: 'good',
                 lead: band ? fill(T['room_tone_' + band], { floor: fl }) : null,
                 text: T['rug_tone_' + way + (strong ? '_strong' : '')],
                 rider: T['rider_tone_' + way],
                 v: f.floor });
    }

    /* An EXACT style match is a different sentence from a compatible one -
     * "modern rugs sit naturally in a modern room" is a tautology a reader
     * notices. */
    if (p.st && room.style && String(p.st).toLowerCase() === String(room.style).toLowerCase()) {
      out.push({ axis: 'style', kind: 'good',
                 lead: fill(T.room_style, { room_style: room.style }),
                 text: fill(T.rug_style_exact,
                            { rug_style: String(p.st).toLowerCase() }),
                 rider: fill(T.rider_style_exact,
                             { rug_style: String(p.st).toLowerCase() }),
                 v: 1 });
    } else if (f.style !== null && f.style >= 0.85 && p.st) {
      /* The RUG style leads the sentence and is capitalised; the ROOM style
       * sits mid-sentence and is not. Names arrive lowercase from the
       * catalogue, and every other clause here starts with a capital. */
      out.push({ axis: 'style', kind: 'good',
                 lead: fill(T.room_style,
                            { room_style: room.style || idx.reason_room_fallback }),
                 text: fill(T.rug_style_near, { rug_style: cap(p.st) }),
                 rider: fill(T.rider_style_near, { rug_style: cap(p.st) }),
                 v: f.style });
    }

    /* THE CLAUSE REQUIRES THE RUG TO ACTUALLY CARRY THAT COLOUR. hFamily
     * returns neutral_cap (0.85) when EITHER side is neutral, and 0.85 also
     * satisfied the old chip's own >= 0.85 test - so 68% of the times it fired,
     * across the 40 gate tuples, it told a customer a NEUTRAL rug "picks up the
     * red already in your room". The condition is on the fact now. */
    if (f.family !== null && p.hue && room.colourFamily &&
        p.hue === room.colourFamily && p.hue !== 'neutral') {
      out.push({ axis: 'family', kind: 'good',
                 lead: fill(T.room_family, { family: room.colourFamily }),
                 text: fill(T.rug_family, { family: room.colourFamily }),
                 rider: fill(T.rider_family, { family: room.colourFamily }),
                 v: 1 });
    }

    /* ACCENT - A COLOUR ON A FLOOR THAT HAS NONE, and gated on the two FACTS
     * rather than on `acc`, which returns 1.0 for exactly this case and 0.90
     * for its mirror image. The mirror is not worth a sentence: a neutral rug
     * in a colourful room is the ordinary case, not an observation. */
    if (f.accent !== null && room.isNeutral && p.neu === false &&
        p.hue && p.hue !== 'neutral') {
      var rich = p.C !== null && p.C !== undefined && p.C >= idx.chroma_rich_min;
      out.push({ axis: 'accent', kind: 'good',
                 lead: T.room_accent,
                 text: fill(rich ? T.rug_accent_rich : T.rug_accent_soft,
                            { hue: p.hue }),
                 rider: fill(T.rider_accent, { hue: p.hue }),
                 v: f.accent });
    }

    if (f.pattern !== null && f.pattern >= 0.8) {
      var calm = (p.busy || 0) < 0.4;
      var load = room.patternLoad;
      out.push({ axis: 'pattern', kind: 'good',
                 /* WHICH ROOM OBSERVATION IS TRUE HERE IS THE ROOM'S FACT, NOT
                  * THE RUG'S. A calm rug can be the right answer in a quiet
                  * room too - the term rewards the COMPLEMENT - so the lead is
                  * keyed on the room's own pattern load and the sentence on
                  * the rug's busyness. */
                 lead: (load === null || load === undefined) ? null
                       : (load >= 0.5 ? T.room_pattern_busy : T.room_pattern_calm),
                 text: calm ? T.rug_pattern_calm : T.rug_pattern_busy,
                 rider: calm ? T.rider_pattern_calm : T.rider_pattern_busy,
                 v: f.pattern });
    }

    out.sort(function (a, b) { return b.v - a.v; });

    /* CHROMA IS APPENDED AFTER THE SORT AND CARRIES NO LEAD AND NO TEXT - it
     * is a RIDER and nothing else. It is not one of the three scored terms, so
     * a card must never open by offering it as the reason it ranked where it
     * did; what it is good for is making the second sentence of two cards that
     * lead on the same axis read differently. Appending AFTER the sort is what
     * guarantees it can never be promoted, rather than trusting v === 0. */
    if (p.C !== null && p.C !== undefined && p.neu === false) {
      if (p.C >= idx.chroma_rich_min) {
        out.push({ axis: 'chroma', kind: 'good', lead: null, text: null,
                   rider: T.rider_chroma_rich, v: 0 });
      } else if (p.C < idx.chroma_muted_max) {
        out.push({ axis: 'chroma', kind: 'good', lead: null, text: null,
                   rider: T.rider_chroma_muted, v: 0 });
      }
    }
    return out;
  }


  /* WHICH SHAPE'S ROW SURVIVES when a product has more than one. A DECLARED
   * order, not a sort: "." and the alphabet decide nothing anybody would
   * choose. Mirrors match.py SHAPE_ORDER. */
  var SHAPE_ORDER = ['rectangle', 'runner', 'square'];

  function shapeRank(sh) {
    var i = SHAPE_ORDER.indexOf(sh);
    return i < 0 ? SHAPE_ORDER.length : i;
  }

  /* Of two rows of the SAME PRODUCT, which one the customer should see.
   *
   * THE SCORE CANNOT BREAK THIS TIE AND THAT IS MEASURED. `scoreOne` reads
   * lightness, chroma, hue, style and busyness - every one a property of the
   * ARTWORK, not of the shape it is cut to - so across the live index 248 of
   * 282 multi-shape products arrive at the same number, the same reasons, and
   * differ only in the photograph and the sizes offered.
   *
   * So it is broken on what the customer is actually choosing between: THE
   * BIGGEST RUG EACH SHAPE CAN PUT IN THIS ROOM. In a hallway the runner's
   * 60x300 beats the rectangle pool's 60x150 and the runner wins - the right
   * answer, and the one a shape-name preference gets wrong. */
  function betterRow(a, b, sizesFor) {
    var sa = a.s.score || 0, sb = b.s.score || 0;
    if (sa !== sb) return sa > sb;
    var za = sizesFor(a.p), zb = sizesFor(b.p);
    var aa = za.length ? za[0].w * za[0].l : 0;
    var ab = zb.length ? zb[0].w * zb[0].l : 0;
    if (aa !== ab) return aa > ab;
    /* A WITHHELD IMAGE LOSES A TIE IT WOULD OTHERWISE WIN. `artefact_policy`
     * keeps the flat runner hero attached for the visualiser and out of every
     * gallery, and a result card is a gallery. The verdict travels in the index
     * as `ul` because the POLICY MODULE decided it at build time - this is not
     * a reader re-deciding it from a filename. */
    if (!!a.p.ul !== !!b.p.ul) return !a.p.ul;
    return shapeRank(a.p.sh) < shapeRank(b.p.sh);
  }

  function rank(idx, products, room, opts) {
    opts = opts || {};
    var top = opts.top || 10;
    var maxNeutral = opts.maxNeutral === undefined ? 2 : opts.maxNeutral;
    var maxPerName = opts.maxPerName === undefined ? 1 : opts.maxPerName;
    var sizesFor = opts.sizesFor || function () { return []; };
    var centres = familyCentres(idx);
    var i;

    var scored = [];
    products.forEach(function (p) {
      var s = scoreOne(idx, centres, p, room);
      if (s.score === null) return;            /* nothing computable — not a 0 */
      scored.push({ p: p, s: s });
    });

    /* ── ONE CARD PER PRODUCT, AND IT USED TO BE TWO ────────────────────────
     * The index is one row per (product, SHAPE) and this used to deduplicate on
     * `p.sku` with `maxPerFamily = 2` - a cap named for a colour family that
     * held a parent SKU, and a limit that therefore admitted BOTH SHAPES OF ONE
     * RUG. Measured over 864 simulated room reads: 33.3% of sets showed the
     * same product twice, and in a hallway it was 5 of 10 cards.
     *
     * The HANDLE is the identity - two rows never share one across the live
     * index. A NAME is not: 48 are held by more than one product, Prairie by
     * nine, which is a SECOND way a set reads as duplicates and why
     * `maxPerName` exists beside this collapse.
     *
     * COLLAPSED BEFORE ANYTHING IS RANKED. Doing it after the sort would let a
     * second shape occupy a slot and then be dropped, leaving nine cards in a
     * set that reported itself as ten. */
    var bestOf = {}, keys = [];
    scored.forEach(function (r) {
      var k = r.p.h || r.p.sku || r.p.k;
      if (!(k in bestOf)) { bestOf[k] = r; keys.push(k); return; }
      if (betterRow(r, bestOf[k], sizesFor)) bestOf[k] = r;
    });
    var rows = keys.map(function (k) { return bestOf[k]; });
    rows.sort(function (a, b) {
      var d = (b.s.score || 0) - (a.s.score || 0);
      return d !== 0 ? d : String(a.p.k || '').localeCompare(String(b.p.k || ''));
    });

    /* ── A VETOED RUG NO LONGER FILLS A SLOT ────────────────────────────────
     * It used to sink below the unvetoed ones and then pad the list out to
     * `top`, so a page promising ten recommendations delivered rugs the engine
     * had already judged likely to disappear into the customer's floor - and
     * the resulting order was not monotonic in score, in 24.9% of runs. Fewer
     * honest answers beat ten of which some are known to be wrong. */
    var nVetoed = 0;
    rows.forEach(function (r) { if (r.s.vetoed) nVetoed++; });
    var eligible = rows.filter(function (r) { return !r.s.vetoed; });

    var chosen = [], deferred = [], neutralUsed = 0, nameUsed = {};

    /* THE NAME CAP IS ENFORCED WHERE A ROW IS TAKEN, NOT WHERE IT IS
     * CONSIDERED, and that is not a tidy-up. Checked only in the first pass, a
     * row deferred for the NEUTRAL reason was never name-checked at all - so
     * the backfill put a second "Canyon" back into 116 of 1,584 measured room
     * reads, which is the exact repeat the collapse above removes. One gate,
     * on the way in, and it cannot be walked around. */
    function take(r) {
      var nm = String(r.p.n || r.p.sku || '').toLowerCase();
      if ((nameUsed[nm] || 0) >= maxPerName) return false;
      chosen.push(r);
      nameUsed[nm] = (nameUsed[nm] || 0) + 1;
      if (r.p.neu) neutralUsed++;
      return true;
    }

    for (i = 0; i < eligible.length && chosen.length < top; i++) {
      var r = eligible[i];
      /* THE NEUTRAL QUOTA DEFERS; the name cap simply refuses. A deferred row
       * may come back if the quota cannot be met honestly - a name-blocked one
       * never does, because taking it IS the duplicate. */
      if (r.p.neu && neutralUsed >= maxNeutral) { deferred.push(r); continue; }
      take(r);
    }

    /* THE QUOTA NEEDS A FLOOR OR IT MANUFACTURES THE BAD RECOMMENDATION IT WAS
     * INSTALLED TO PREVENT. This catalogue is 79% neutral, so without a quota
     * the page IS ten cream rugs. But promoting a materially worse rug to fill
     * it is exactly the wrong recommendation - so when it cannot be met
     * honestly the page says so instead. */
    var quotaUnmet = false;
    if (chosen.length < top && deferred.length) {
      for (i = 0; i < deferred.length && chosen.length < top; i++) {
        take(deferred[i]);
      }
      quotaUnmet = true;
    }

    /* ── THE ORDER SHOWN IS THE ORDER OF THE SCORES, AND IT WAS NOT ────────
     * The neutral quota DEFERS excess neutrals and backfills them at the end,
     * so a deferred neutral scoring 6.54 landed BELOW a chromatic rug scoring
     * 6.53. Measured over 7,056 simulated room reads on the live index: 390
     * sets (5.5%) were not in score order, and the worst inversion showed 3.1
     * above 5.7 - a 2.7-point contradiction on a page that now prints both
     * numbers and colours them by position.
     *
     * THE QUOTA DECIDES MEMBERSHIP; THE SORT DECIDES ORDER. Two questions, and
     * conflating them is the whole of the bug. Re-sorting changes nothing
     * about WHO is chosen - `chosen` is already final - and it is the same
     * tie-break key the main sort uses, so two rugs on an identical score
     * cannot swap between the engines. */
    chosen.sort(function (a, b) {
      var d = (b.s.score || 0) - (a.s.score || 0);
      return d !== 0 ? d : String(a.p.k || '').localeCompare(String(b.p.k || ''));
    });

    var setSize = chosen.length;
    var items = chosen.map(function (r, n) {
      /* THE COLOUR IS A FIXED DISTRIBUTION, NOT A THRESHOLD, and it is a
       * statement about POSITION rather than about quality - see match.py's
       * `tier_of`, which this mirrors and which carries the reasoning. Rank 1
       * is green, the bottom `floor(n x 0.2)` are red, the rest yellow: 1/7/2
       * at ten cards, and red vanishing on its own below five because that is
       * where the floor function reaches 1. */
      var rk = n + 1;
      var lowFrom = setSize - Math.floor(setSize * idx.tier_low_fraction);
      return { product: r.p, score: r.s.score, vetoed: r.s.vetoed,
               parts: r.s.parts, rank: rk, setSize: setSize,
               tier: rk <= idx.tier_best_ranks ? 'best'
                   : rk > lowFrom ? 'low' : 'strong',
               why: why(idx, r.p, room, r.s) };
    });

    var vals = items.map(function (x) { return x.score; })
                    .filter(function (v) { return v !== null && v !== undefined; });
    var spread = vals.length > 1 ? Math.max.apply(null, vals) - Math.min.apply(null, vals) : 0;

    return {
      items: items,
      considered: products.length,
      scored: scored.length,
      products: rows.length,
      vetoed: nVetoed,
      eligible: eligible.length,
      /* FEWER THAN ASKED FOR, SAID OUT LOUD rather than padded. */
      short: chosen.length < top,
      asked: top,
      spread: spread,
      /* WHEN THE TEN ARE INDISTINGUISHABLE THE ORDER IS NOT A FINDING, and the
       * page says so once above the grid instead of implying a ranking. */
      close: !!(vals.length > 1 && spread < idx.close_spread),
      /* WHEN EVERYTHING WE HAVE IS VETOED there is no "below" to demote to, and
       * no ordering of bad answers is a good answer. */
      allVetoed: !!rows.length && nVetoed === rows.length,
      none: !chosen.length,
      quotaUnmet: quotaUnmet,
      quotaNote: quotaUnmet
        ? 'only neutral rugs suit a room like yours — the colourful ones we ' +
          'have would fight your floor'
        : null,
      matchVersion: idx.match_version
    };
  }


  /* ── THE SIZE BAND: ONE DEFINITION, TWO CALLERS (2026-09-05) ─────────────
   * These four functions were inside `rug-finder.js`, where they had exactly
   * one caller. The visualiser popup now names a size too, from the same room
   * read, so there are two - and the brief that asked for it said so in as
   * many words: "use the Rug Finder's own band calculation rather than a
   * second one - one definition, two callers."
   *
   * NOTHING HERE EVALUATES A SIZING RULE. `placement.py` computes every
   * (room, placement, input) band at BUILD time and ships them in the index;
   * this is a LOOKUP into that table. That was already the arrangement and it
   * is the reason a second caller is cheap: the rules cannot drift between the
   * two surfaces because neither surface holds them.
   *
   * They live in fit-engine.js rather than in a new file because this is
   * already the shared engine both clients load, and it is already the thing a
   * conformance test holds against the Python. A third asset would be a third
   * place to remember. */

  /* WHICH CELL OF THE TABLE THIS ROOM READ ADDRESSES. The room analyser and
   * the visualiser's own gate answer with the SAME field names and the same
   * vocabularies - deliberately, and the gate's descriptions are copied from
   * the analyser's for exactly this reason - so one mapping serves both. */
  function inputKeyFor(room, r) {
    r = r || {};
    if (room === 'living') {
      /* A seat COUNT is what a photo can give. It maps to a length band, and
       * the CONSERVATIVE end of that band is used. */
      var seats = r.sofa_seats || 3;
      return String(Math.min(400, Math.max(150, 70 * seats + 20)));
    }
    if (room === 'bedroom') {
      var m = { single: 'single', double: 'double_tr', queen: 'queen_tr',
                king: 'king_tr' };
      return m[r.bed_size] || 'queen_tr';
    }
    if (room === 'dining') {
      var n = r.table_seats || 6;
      return String([4, 6, 8, 10].reduce(function (best, k) {
        return Math.abs(k - n) < Math.abs(best - n) ? k : best;
      }, 6));
    }
    if (room === 'office') return 'standard';
    return '120';                 /* hallway / kitchen: a common width */
  }

  function bandFor(idx, room, placement, key) {
    if (!idx || !idx.bands || !idx.bands.cells) return null;
    var slot = idx.bands.cells[room + '|' + placement];
    if (!slot) return null;
    var cell = slot[String(key)];
    if (!cell) {
      /* Nearest available input rather than nothing: the grid is 10 cm and a
       * read of 213 must not fall through a gap. */
      var keys = Object.keys(slot).filter(function (k) { return /^\d+$/.test(k); });
      if (!keys.length) return null;
      var want = parseFloat(key);
      keys.sort(function (a, b) {
        return Math.abs(a - want) - Math.abs(b - want);
      });
      cell = slot[keys[0]];
    }
    if (cell.no) return { ok: false, reason: cell.no, alternatives: cell.alt || [] };
    var t = (idx.bands.texts || {})[room + '|' + placement] || {};
    return { ok: true, along: cell.a, across: cell.c, ideal: cell.i,
             warning: cell.w || null, rule: t.rule, sources: t.sources,
             notes: t.notes };
  }

  /* THE SIZE STRING IS THE CATALOGUE'S OWN, imperial-first with the metric in
   * the bracket, and the centimetres are read straight out of it. Nothing is
   * converted and nothing is rounded, so the number a customer reads and the
   * string that names the variant cannot drift apart. */
  function parseSizes(list) {
    var out = [];
    (list || []).forEach(function (s) {
      var m = /(\d+)\s*[xX]\s*(\d+)\s*cm/.exec(s);
      if (m) out.push({ w: +m[1], l: +m[2], option: s });
    });
    return out;
  }

  /* ORIENTATION-AGNOSTIC ON PURPOSE: a 160x230 and a 230x160 are the same rug
   * turned round, and the band describes the FLOOR, not the label. */
  function fits(w, l, a, c) {
    return (a[0] <= w && w <= a[1] && c[0] <= l && l <= c[1]) ||
           (a[0] <= l && l <= a[1] && c[0] <= w && w <= c[1]);
  }

  /* LARGEST FIRST - an oversized rug reads as a design choice, an undersized
   * one as a mistake. Measured: sizing to the top of the estimate takes "too
   * small" from 18.6% to 0.6%. */
  function sizesInBand(list, b) {
    if (!b || !b.ok) return [];
    var hits = parseSizes(list).filter(function (s) {
      return fits(s.w, s.l, b.along, b.across);
    });
    hits.sort(function (x, y) { return (y.w * y.l) - (x.w * x.l); });
    return hits;
  }

  global.FitEngine = {
    hFloor: hFloor, probit: probit, floorPosterior: floorPosterior,
    contrastScore: contrastScore, hFamily: hFamily, hAccent: hAccent,
    hPattern: hPattern, styleScore: styleScore, scoreOne: scoreOne,
    familyCentres: familyCentres, rank: rank,
    why: why, floorPhrase: floorPhrase, fill: fill,
    inputKeyFor: inputKeyFor, bandFor: bandFor, parseSizes: parseSizes,
    fits: fits, sizesInBand: sizesInBand
  };
}(typeof globalThis !== 'undefined' ? globalThis : this));
